import React, { useState, useRef, useEffect } from 'react';
import { Camera, X, RotateCcw, CheckCircle } from 'lucide-react';
import { BrowserMultiFormatReader, Result } from '@zxing/library';

const BarcodeScanner = ({ onScan, onClose, isOpen }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [detectedCode, setDetectedCode] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const codeReaderRef = useRef(null);

  useEffect(() => {
    if (isOpen && !scanning) {
      startScanning();
    }
    return () => {
      stopScanning();
    };
  }, [isOpen]);

  const startScanning = async () => {
    try {
      setError(null);
      setScanning(true);
      setDetectedCode(null);
      
      // Initialize ZXing reader
      codeReaderRef.current = new BrowserMultiFormatReader();
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      // Start barcode detection with ZXing
      startBarcodeDetection();
    } catch (err) {
      setError('Failed to access camera. Please check permissions.');
      console.error('Camera error:', err);
    }
  };

  const stopScanning = () => {
    setScanning(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
  };

  const startBarcodeDetection = async () => {
    if (!codeReaderRef.current || !videoRef.current) return;
    
    try {
      // Start continuous scanning
      await codeReaderRef.current.decodeFromVideoDevice(
        undefined, // Use default camera
        videoRef.current,
        (result, error) => {
          if (result) {
            // Barcode detected!
            console.log('🎯 Barcode detected:', result.text);
            setDetectedCode(result.text);
            setScanning(false);
            
            // Stop scanning and close after a short delay
            setTimeout(() => {
              onScan(result.text);
              stopScanning();
            }, 1000);
          }
          
          if (error && error.name !== 'NotFoundException') {
            console.log('Scanning...', error.message);
          }
        }
      );
    } catch (err) {
      console.error('Barcode detection error:', err);
      setError('Failed to start barcode detection');
    }
  };

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  const handleRetry = () => {
    stopScanning();
    startScanning();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Scan Barcode</h3>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {error ? (
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={handleRetry}
              className="btn bg-blue-600 hover:bg-blue-700 text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        ) : (
          <div className="relative">
            <video
              ref={videoRef}
              className="w-full h-64 object-cover rounded-lg"
              autoPlay
              playsInline
              muted
            />
            
            {/* Scanning overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="border-2 border-green-500 rounded-lg p-2">
                <div className="w-48 h-32 border-2 border-white rounded"></div>
              </div>
            </div>
            
            {/* Barcode detected indicator */}
            {detectedCode && (
              <div className="absolute inset-0 flex items-center justify-center bg-green-500 bg-opacity-75 rounded-lg">
                <div className="text-center text-white">
                  <CheckCircle className="w-16 h-16 mx-auto mb-2" />
                  <p className="text-lg font-semibold">Barcode Detected!</p>
                  <p className="text-sm opacity-90">{detectedCode}</p>
                </div>
              </div>
            )}
            
            {/* Instructions */}
            <div className="mt-4 text-center text-sm text-gray-600">
              <p>Position the barcode within the frame</p>
              <p className="mt-1">The scanner will automatically detect barcodes</p>
              {scanning && (
                <p className="mt-2 text-green-600 font-medium">Scanning...</p>
              )}
            </div>
          </div>
        )}
        
        <div className="mt-4 flex justify-center space-x-3">
          <button
            onClick={handleClose}
            className="btn btn-outline"
          >
            Cancel
          </button>
          
          {/* Manual input for testing */}
          <button
            onClick={() => onScan('1234567890123')}
            className="btn bg-green-600 hover:bg-green-700 text-white"
            title="Test with sample barcode"
          >
            Test Barcode
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
